/**
 * Herstellink uit de e-mail — detectie
 *
 * Supabase stuurt bij "wachtwoord vergeten" een mail met een link naar
 * `<origin>/#access_token=…&type=recovery`. Die tokens staan in de URL-HASH en
 * niet in de query, omdat een hash niet naar de server gaat.
 *
 * WAAROM DIT OP MODULE-NIVEAU GEBEURT EN NIET IN EEN EFFECT
 *
 * `supabase-js` staat standaard op `detectSessionInUrl`. Dat betekent dat de
 * client de hash zelf leest, er een sessie van maakt en de hash daarna WIST uit
 * de adresbalk. Dat gebeurt zodra de client geïnitialiseerd wordt — dus vóór
 * React zijn eerste effect draait.
 *
 * Zou dit in een `useEffect` staan, dan was de hash er soms al niet meer en
 * werkte de herstellink "meestal". Dat is de vervelendste faalvorm: hij hangt af
 * van welke module als eerste geïmporteerd wordt.
 *
 * Daarom leest deze module de hash SYNCHROON bij het laden en bewaart alleen de
 * conclusie. De tokens zelf houden we niet vast — Supabase heeft de sessie al,
 * en een token dat wij ook nog ergens bewaren is een tweede plek waar hij kan
 * uitlekken.
 */

/** Was dit paginabezoek een herstellink uit een e-mail? */
const isRecoveryLanding = readRecoveryFlagFromHash();

/**
 * Onderscheid tussen "een geldige herstellink" en "een kapotte link".
 *
 * Supabase zet bij een verlopen of al gebruikte link geen tokens maar een
 * `error`/`error_description` in de hash. Dat is een ANDER geval dan een gewone
 * paginalading, en de gebruiker moet het verschil weten: bij een verlopen link
 * moet hij een nieuwe aanvragen, niet nog eens klikken.
 */
const recoveryError = readRecoveryErrorFromHash();
function readRecoveryFlagFromHash(): boolean {
  try {
    const hash = window.location.hash.replace(/^#/, '');
    if (!hash) return false;
    const params = new URLSearchParams(hash);
    return params.get('type') === 'recovery' && Boolean(params.get('access_token'));
  } catch {
    return false;
  }
}
function readRecoveryErrorFromHash(): string | null {
  try {
    const hash = window.location.hash.replace(/^#/, '');
    if (!hash) return null;
    const params = new URLSearchParams(hash);
    const code = params.get('error_code') ?? params.get('error');
    if (!code) return null;

    // De omschrijving van Supabase is Engels en technisch. Voor de twee gevallen
    // die echt voorkomen geven we een eigen tekst; de rest valt terug op iets
    // neutraals in plaats van een Engelse foutcode.
    const raw = (params.get('error_description') ?? code).toLowerCase();
    if (raw.includes('expired')) {
      return 'Deze herstellink is verlopen. Vraag een nieuwe aan — een link is een uur geldig.';
    }
    if (raw.includes('already') || raw.includes('used')) {
      return 'Deze herstellink is al gebruikt. Vraag een nieuwe aan als je je wachtwoord nog wilt wijzigen.';
    }
    return 'Deze herstellink werkt niet meer. Vraag een nieuwe aan.';
  } catch {
    return null;
  }
}

/**
 * Moet de app het "nieuw wachtwoord"-scherm tonen?
 *
 * Ook `true` bij een kapotte link: dan is er iets uit te leggen. Zonder dat
 * landt de gebruiker op het inlogscherm zonder enig idee waarom zijn link niets
 * deed — en probeert hij het opnieuw met dezelfde dode link.
 */
export function isPasswordRecoveryLanding(): boolean {
  return isRecoveryLanding || recoveryError !== null;
}

/** De uitlegtekst bij een verlopen of gebruikte link, of `null` bij een geldige. */
export function getRecoveryLinkError(): string | null {
  return recoveryError;
}

/**
 * Haalt de tokens uit de adresbalk.
 *
 * Supabase doet dit zelf ook, maar niet in elke configuratie — en een
 * access-token in de adresbalk komt terecht in de browsergeschiedenis en in elke
 * link die de gebruiker daarna deelt. Dus altijd zelf opruimen zodra het scherm
 * staat.
 *
 * `replaceState` en niet `location.hash = ''`: dat laatste laat een `#` staan en
 * voegt een geschiedenisregel toe, waardoor de terug-knop de gebruiker opnieuw
 * op de herstelpagina zet.
 */
export function clearRecoveryHash(): void {
  try {
    window.history.replaceState(null, '', window.location.pathname + window.location.search);
  } catch {
    // Niet kritiek: het scherm werkt ook met de hash er nog in.
  }
}
